import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Send } from 'lucide-react';

const CONTACT_EMAIL = 'miralis-app@outlook.com';

export default function ContactPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [formData, setFormData] = useState({ subject: '', body: '' });

  const handleSendEmail = () => {
    const subject = encodeURIComponent(formData.subject || (language === 'fr' ? 'Contact MyCompanion AI' : 'MyCompanion AI Contact'));
    const body = encodeURIComponent(formData.body);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-velvet-bg px-4 py-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-velvet-bg via-velvet-bg to-purple-dark/20" />
      <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-fuchsia-main/10 rounded-full blur-3xl" />
      
      <div className="relative z-10 max-w-lg mx-auto">
        <button 
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent mb-8 transition-colors"
          data-testid="contact-back-button"
        >
          <ArrowLeft size={20} />
          {language === 'fr' ? 'Retour' : language === 'es' ? 'Volver' : language === 'de' ? 'Zurück' : language === 'pt' ? 'Voltar' : 'Back'}
        </button>
        
        <div className="glass rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-purple-main/20 flex items-center justify-center">
              <Mail size={24} className="text-purple-light" />
            </div>
            <div>
              <h1 className="font-heading text-2xl text-velvet_text-primary" data-testid="contact-title">
                {language === 'fr' ? 'Contactez-nous' : language === 'es' ? 'Contáctenos' : language === 'de' ? 'Kontaktieren Sie uns' : language === 'pt' ? 'Contacte-nos' : 'Contact Us'}
              </h1>
              <p className="text-velvet_text-secondary text-sm">{CONTACT_EMAIL}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-velvet_text-secondary">
                {language === 'fr' ? 'Sujet' : language === 'es' ? 'Asunto' : language === 'de' ? 'Betreff' : language === 'pt' ? 'Assunto' : 'Subject'}
              </Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="bg-velvet-paper border-white/10 text-velvet_text-primary h-12"
                placeholder={language === 'fr' ? 'Objet de votre message' : 'Subject of your message'}
                data-testid="contact-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-velvet_text-secondary">
                {language === 'fr' ? 'Message' : language === 'es' ? 'Mensaje' : language === 'de' ? 'Nachricht' : language === 'pt' ? 'Mensagem' : 'Message'}
              </Label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                className="w-full bg-velvet-paper border border-white/10 text-velvet_text-primary rounded-lg p-3 min-h-[160px] resize-none focus:outline-none focus:ring-2 focus:ring-purple-main/50"
                placeholder={language === 'fr' ? 'Votre message...' : 'Your message...'}
                data-testid="contact-body-input"
              />
            </div>
            
            <Button
              onClick={handleSendEmail}
              className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-full h-12 font-semibold shadow-neon"
              data-testid="contact-send-button"
            >
              <Send size={18} className="mr-2" />
              {language === 'fr' ? 'Envoyer par email' : language === 'es' ? 'Enviar por email' : language === 'de' ? 'Per E-Mail senden' : language === 'pt' ? 'Enviar por email' : 'Send via email'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
